import type { RouteGraphEdge, StablePath } from "./types.ts";

interface Neighbor {
  readonly nodeId: string;
  readonly edgeId: string;
  readonly costMilliWU: number;
}

function ordinalCompare(left: string, right: string): number {
  return left < right ? -1 : left > right ? 1 : 0;
}

function addSafeCost(left: number, right: number): number | null {
  if (
    !Number.isSafeInteger(left) ||
    left < 0 ||
    !Number.isSafeInteger(right) ||
    right <= 0 ||
    left > Number.MAX_SAFE_INTEGER - right
  ) return null;
  return left + right;
}

function neighborsFor(edges: readonly RouteGraphEdge[], nodeId: string): Neighbor[] {
  const neighbors: Neighbor[] = [];
  for (const edge of edges) {
    if (edge.from === nodeId) neighbors.push({ nodeId: edge.to, edgeId: edge.id, costMilliWU: edge.costMilliWU });
    else if (edge.to === nodeId) neighbors.push({ nodeId: edge.from, edgeId: edge.id, costMilliWU: edge.costMilliWU });
  }
  neighbors.sort((left, right) => ordinalCompare(left.nodeId, right.nodeId) || ordinalCompare(left.edgeId, right.edgeId));
  return neighbors;
}

export function findStablePath(
  nodeIds: readonly string[],
  edges: readonly RouteGraphEdge[],
  fromNodeId: string,
  toNodeId: string,
): Readonly<StablePath> | null {
  if (!nodeIds.includes(fromNodeId) || !nodeIds.includes(toNodeId)) return null;
  if (fromNodeId === toNodeId) {
    return Object.freeze({ nodeIds: Object.freeze([fromNodeId]), edgeIds: Object.freeze([]), totalCostMilliWU: 0 });
  }

  const bestCost = new Map<string, number>([[fromNodeId, 0]]);
  const predecessor = new Map<string, Readonly<{ nodeId: string; edgeId: string }>>();
  const closed = new Set<string>();
  const open: Array<Readonly<{ nodeId: string; gCostMilliWU: number }>> = [
    Object.freeze({ nodeId: fromNodeId, gCostMilliWU: 0 }),
  ];

  while (open.length > 0) {
    open.sort((left, right) => left.gCostMilliWU - right.gCostMilliWU || ordinalCompare(left.nodeId, right.nodeId));
    const current = open.shift()!;
    if (closed.has(current.nodeId)) continue;
    /* v8 ignore next -- a stale entry for a settled node is consumed by the closed check above. */
    if (current.gCostMilliWU !== bestCost.get(current.nodeId)) continue;
    if (current.nodeId === toNodeId) break;
    closed.add(current.nodeId);
    for (const neighbor of neighborsFor(edges, current.nodeId)) {
      if (closed.has(neighbor.nodeId)) continue;
      const newCost = addSafeCost(current.gCostMilliWU, neighbor.costMilliWU);
      if (newCost === null) continue;
      const priorCost = bestCost.get(neighbor.nodeId);
      if (priorCost !== undefined && newCost >= priorCost) continue;
      bestCost.set(neighbor.nodeId, newCost);
      predecessor.set(neighbor.nodeId, Object.freeze({ nodeId: current.nodeId, edgeId: neighbor.edgeId }));
      open.push(Object.freeze({ nodeId: neighbor.nodeId, gCostMilliWU: newCost }));
    }
  }

  const totalCostMilliWU = bestCost.get(toNodeId);
  if (totalCostMilliWU === undefined || !predecessor.has(toNodeId)) return null;
  const reversedNodes = [toNodeId];
  const reversedEdges: string[] = [];
  let cursor = toNodeId;
  while (cursor !== fromNodeId) {
    const step = predecessor.get(cursor);
    /* v8 ignore next -- a reachable non-origin node is assigned a predecessor during relaxation. */
    if (step === undefined) return null;
    reversedEdges.push(step.edgeId);
    reversedNodes.push(step.nodeId);
    cursor = step.nodeId;
  }
  return Object.freeze({
    nodeIds: Object.freeze(reversedNodes.reverse()),
    edgeIds: Object.freeze(reversedEdges.reverse()),
    totalCostMilliWU,
  });
}
